/**
 * Visual Page Builder - Component Registry
 * 
 * Central registry of allowed components with Zod schemas for validation
 * and auto-generated property forms. Based on functional spec examples.
 */

import { z } from 'zod';
import { ComponentType } from '../types/page-builder.types';

// Component metadata for the editor UI
export type ComponentMeta = {
  label: string;
  description: string;
  icon: string;
  category: 'layout' | 'content' | 'media' | 'data' | 'interactive';
  schema: z.ZodSchema;
  defaultProps: Record<string, unknown>;
  previewComponent?: React.ComponentType<any>;
};

// Registry definition following functional spec examples
export const ComponentRegistry: Record<ComponentType, ComponentMeta> = {
  RichText: {
    label: 'Rich Text',
    description: 'Full WYSIWYG rich text editor with formatting toolbar',
    icon: 'text_format',
    category: 'content',
    schema: z.object({
      // Content properties
      html: z.string().default('<p>Enter your text here...</p>'),
      placeholder: z.string().optional().default('Start typing...'),

      // Container styling properties (for property panel)
      minHeight: z.coerce.number().min(0).max(800).optional().default(150),
      backgroundColor: z.string().optional(),
      borderColor: z.string().default('#e0e0e0'),
      borderWidth: z.coerce.number().min(0).max(10).default(1),
      borderStyle: z.enum(['solid', 'dashed', 'dotted', 'none']).default('solid'),
      borderRadius: z.coerce.number().min(0).max(50).default(4),
      padding: z.coerce.number().min(0).max(100).default(16)
    }),
    defaultProps: {
      html: '<p>Enter your text here...</p>',
      placeholder: 'Start typing...',
      minHeight: 20,
      borderColor: '#e0e0e0',
      borderWidth: 0,
      borderStyle: 'solid',
      borderRadius: 4,
      padding: 0
    }
  },

  Image: {
    label: 'Image',
    description: 'Display images with alt text and styling',
    icon: 'image',
    category: 'media',
    schema: z.object({
      // Image properties
      src: z.string().url('Must be a valid URL'),
      alt: z.string().default(''),
      href: z.string().describe('Makes the image a clickable link').optional(),
      openLinkIn: z.enum(['sameTab', 'newTab']).describe('Where the link opens when the image is clicked').default('sameTab'),
      width: z.coerce.number().positive().optional(),
      height: z.coerce.number().positive().optional(),
      caption: z.string().optional(),
      alignment: z.enum(['left', 'center', 'right']).default('center'),

      // Container styling properties (same as RichText)
      backgroundColor: z.string().optional(),
      borderColor: z.string().default('#e0e0e0'),
      borderWidth: z.coerce.number().min(0).max(10).default(0),
      borderStyle: z.enum(['solid', 'dashed', 'dotted', 'none']).default('none'),
      borderRadius: z.coerce.number().min(0).max(50).default(4),
      padding: z.coerce.number().min(0).max(100).default(8)
    }),
    defaultProps: {
      src: 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="200"%3E%3Crect width="400" height="200" fill="%23e0e0e0"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="Arial, sans-serif" font-size="18" fill="%23666"%3EImage Placeholder%3C/text%3E%3C/svg%3E',
      alt: '',
      openLinkIn: 'sameTab',
      alignment: 'center',
      borderColor: '#e0e0e0',
      borderWidth: 0,
      borderStyle: 'none',
      borderRadius: 4,
      padding: 8
    }
  },

  Divider: {
    label: 'Divider',
    description: 'Visual separator between content sections with customizable styling',
    icon: 'horizontal_rule',
    category: 'layout',
    schema: z.object({
      // Basic divider properties
      variant: z.enum(['fullWidth', 'middle', 'inset']).default('fullWidth'),
      orientation: z.enum(['horizontal', 'vertical']).default('horizontal'),
      thickness: z.coerce.number().min(1).max(20).default(1),
      color: z.string().default('#e0e0e0'),
      backgroundColor: z.string().optional(),

      // Style properties
      style: z.enum(['solid', 'dashed', 'dotted', 'double', 'ridge', 'groove']).default('solid'),

      // Spacing properties
      marginTop: z.coerce.number().min(0).max(100).default(16),
      marginBottom: z.coerce.number().min(0).max(100).default(16),
      marginLeft: z.coerce.number().min(0).max(100).default(0),
      marginRight: z.coerce.number().min(0).max(100).default(0),
      paddingTop: z.coerce.number().min(0).max(100).default(0),
      paddingBottom: z.coerce.number().min(0).max(100).default(0),
      paddingLeft: z.coerce.number().min(0).max(100).default(0),
      paddingRight: z.coerce.number().min(0).max(100).default(0),

      // Border radius for rounded dividers
      borderRadius: z.coerce.number().min(0).max(50).default(0),

      // Opacity for transparency effects
      opacity: z.coerce.number().min(0.1).max(1).default(1),
      
      // Width control (percentage for horizontal, height for vertical)
      width: z.string().default('100%'),
      height: z.string().default('auto'),
      
      // Shadow effect
      boxShadow: z.string().optional(),
      
      // Alignment within container
      textAlign: z.enum(['left', 'center', 'right']).default('center')
    }),
    defaultProps: {
      variant: 'fullWidth',
      orientation: 'horizontal',
      thickness: 1,
      color: '#e0e0e0',
      style: 'solid',
      marginTop: 16,
      marginBottom: 16,
      marginLeft: 0,
      marginRight: 0,
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
      borderRadius: 0,
      opacity: 1,
      width: '100%',
      height: 'auto',
      textAlign: 'center'
    }
  },

  // Container: {
  //   label: 'Container',
  //   description: 'Wrapper with styling options',
  //   icon: 'crop_free',
  //   category: 'layout',
  //   schema: z.object({
  //     variant: z.enum(['card', 'paper', 'outlined', 'none']).default('card'),
  //     padding: z.number().min(0).max(64).default(16),
  //     backgroundColor: z.string().optional(),
  //     borderRadius: z.number().min(0).max(32).default(8),
  //     shadow: z.enum(['none', 'small', 'medium', 'large']).default('small')
  //   }),
  //   defaultProps: {
  //     variant: 'card',
  //     padding: 16,
  //     borderRadius: 8,
  //     shadow: 'small'
  //   }
  // },


  ViewContentPick: {
    label: 'Content Pick',
    description: 'Display and manage multiple selected content pieces from library',
    icon: 'view_module',
    category: 'content',
    schema: z.object({
      contentIds: z.array(z.string()).default([]),
      displayMode: z.enum(['grid', 'list', 'tile', 'minimal']).default('grid'),
      bannerStyle: z.enum(['thumbnails', 'illustrations']).default('illustrations'),
      maxItems: z.coerce.number().min(1).max(50).default(12),
      columns: z.coerce.number().min(1).max(12).default(3),
      showMetadata: z.boolean().default(true),
      showIcon: z.boolean().default(true),
      showActions: z.boolean().default(true),
      showRating: z.boolean().default(true),
      showDescription: z.boolean().default(true),
      enableContentSelector: z.boolean().default(true),
      // Container styling properties
      backgroundColor: z.string().optional(),
      borderColor: z.string().default('#e0e0e0'),
      borderWidth: z.coerce.number().min(0).max(10).default(0),
      borderStyle: z.enum(['solid', 'dashed', 'dotted', 'none']).default('none'),
      borderRadius: z.coerce.number().min(0).max(50).default(8),
      padding: z.coerce.number().min(0).max(100).default(1),
      gap: z.coerce.number().min(0).max(50).default(1),
      // Size and scrolling options (use Style Settings for width/height of outer container)
      containerHeight: z.string().optional().describe('Fixed height for scrolling (e.g., 400px, 50vh, calc(100vh - 200px))'),
      enableScroll: z.boolean().default(false).describe('Enable scrolling when content exceeds container height')
    }),
    defaultProps: {
      contentIds: [],
      displayMode: 'grid',
      bannerStyle: 'illustrations',
      maxItems: 12,
      columns: 3,
      showMetadata: true,
      showIcon: true,
      showActions: true,
      showRating: true,
      showDescription: true,
      enableContentSelector: true,
      borderColor: '#e0e0e0',
      borderWidth: 0,
      borderStyle: 'none',
      borderRadius: 8,
      padding: 1,
      gap: 1,
      enableScroll: false
    }
  },
  ViewContentQuery: {
    label: 'Content Query',
    description: 'Display content based on dynamic search queries and filters',
    icon: 'search',
    category: 'content',
    schema: z.object({
      // Query configuration
      searchQuery: z.string().default(''),
      filters: z.object({
        mimeTypes: z.array(z.string()).optional(),
        tags: z.array(z.string()).optional(),
        systemTags: z.array(z.string()).optional(),
        personalTags: z.array(z.string()).optional(),
        authors: z.array(z.string()).optional(),
        organizations: z.array(z.string()).optional(),
        teams: z.array(z.string()).optional(),
        bookmarkedOnly: z.boolean().optional(),
        personalTagsOnly: z.boolean().optional(),
        systemTagsOnly: z.boolean().optional(),
        funnelStages: z.array(z.string()).optional(),
        personas: z.array(z.string()).optional()
      }).default({}),
      sortBy: z.enum(['updatedAt', 'createdAt', 'title', 'rating', 'views', 'downloads']).default('updatedAt'),
      sortOrder: z.enum(['asc', 'desc']).default('desc'),

      // Display configuration - matching ViewContentPick
      displayMode: z.enum(['grid', 'list', 'tile', 'minimal']).default('grid'),
      bannerStyle: z.enum(['thumbnails', 'illustrations']).default('illustrations'),
      // Hard cap on the total number of items the component will display
      // across all pages. Authors who want a small fixed set (e.g. "top 12
      // most-recently-updated") leave the default; authors who want a full
      // pagination experience bump it. Display pagination is computed
      // against `min(serverTotalResults, maxItems)`.
      maxItems: z.coerce.number().min(1).max(1000).default(100),
      columns: z.coerce.number().min(1).max(12).default(3),
      showMetadata: z.boolean().default(true),
      showIcon: z.boolean().default(true),
      showActions: z.boolean().default(true),
      showRating: z.boolean().default(true),
      showDescription: z.boolean().default(true),
      enableQueryBuilder: z.boolean().default(true),

      // Per-page item count for the pagination controls. The control auto-
      // shows when the effective total (capped by maxItems) exceeds this.
      pageSize: z.coerce.number().min(5).max(100).default(25),

      // Auto-refresh
      autoRefresh: z.boolean().default(false),
      refreshInterval: z.coerce.number().min(10).max(3600).default(60),

      // Container styling properties
      backgroundColor: z.string().optional(),
      borderColor: z.string().default('#e0e0e0'),
      borderWidth: z.coerce.number().min(0).max(10).default(0),
      borderStyle: z.enum(['solid', 'dashed', 'dotted', 'none']).default('none'),
      borderRadius: z.coerce.number().min(0).max(50).default(8),
      padding: z.coerce.number().min(0).max(100).default(1),
      gap: z.coerce.number().min(0).max(50).default(1),
      // Size and scrolling options (use Style Settings for width/height of outer container)
      containerHeight: z.string().optional().describe('Fixed height for scrolling (e.g., 400px, 50vh, calc(100vh - 200px))'),
      enableScroll: z.boolean().default(false).describe('Enable scrolling when content exceeds container height')
    }),
    defaultProps: {
      searchQuery: '',
      filters: {},
      sortBy: 'updatedAt',
      sortOrder: 'desc',
      displayMode: 'grid',
      bannerStyle: 'illustrations',
      maxItems: 100,
      columns: 3,
      showMetadata: true,
      showIcon: true,
      showActions: true,
      showRating: true,
      showDescription: true,
      enableQueryBuilder: true,
      pageSize: 25,
      autoRefresh: false,
      refreshInterval: 60,
      borderColor: '#e0e0e0',
      borderWidth: 0,
      borderStyle: 'none',
      borderRadius: 8,
      padding: 1,
      gap: 1,
      enableScroll: false
    }
  },

  // KPI: {
  //   label: 'KPI Display',
  //   description: 'Show key performance indicators with trends',
  //   icon: 'trending_up',
  //   category: 'data',
  //   schema: z.object({
  //     label: z.string().min(1, 'Label is required'),
  //     value: z.string().min(1, 'Value is required'),
  //     trend: z.enum(['up', 'down', 'flat']).default('flat'),
  //     delta: z.string().optional(),
  //     format: z.enum(['number', 'currency', 'percentage']).default('number'),
  //     size: z.enum(['small', 'medium', 'large']).default('medium'),
  //     color: z.enum(['primary', 'success', 'warning', 'error']).default('primary')
  //   }),
  //   defaultProps: {
  //     label: 'Metric',
  //     value: '0',
  //     trend: 'flat',
  //     format: 'number',
  //     size: 'medium',
  //     color: 'primary'
  //   }
  // },

  // Video: {
  //   label: 'Video',
  //   description: 'Embed videos with controls and styling',
  //   icon: 'play_circle',
  //   category: 'media',
  //   schema: z.object({
  //     src: z.string().url('Must be a valid URL'),
  //     poster: z.string().url().optional(),
  //     width: z.number().positive().optional(),
  //     height: z.number().positive().optional(),
  //     controls: z.boolean().default(true),
  //     autoplay: z.boolean().default(false),
  //     muted: z.boolean().default(false),
  //     loop: z.boolean().default(false)
  //   }),
  //   defaultProps: {
  //     src: '',
  //     controls: true,
  //     autoplay: false,
  //     muted: false,
  //     loop: false
  //   }
  // },

  // Button: {
  //   label: 'Button',
  //   description: 'Interactive button with customizable actions',
  //   icon: 'smart_button',
  //   category: 'interactive',
  //   schema: z.object({
  //     text: z.string().min(1, 'Button text is required'),
  //     variant: z.enum(['contained', 'outlined', 'text']).default('contained'),
  //     color: z.enum(['primary', 'secondary', 'success', 'warning', 'error']).default('primary'),
  //     size: z.enum(['small', 'medium', 'large']).default('medium'),
  //     href: z.string().optional(),
  //     target: z.enum(['_self', '_blank']).default('_self'),
  //     disabled: z.boolean().default(false)
  //   }),
  //   defaultProps: {
  //     text: 'Click me',
  //     variant: 'contained',
  //     color: 'primary',
  //     size: 'medium',
  //     target: '_self',
  //     disabled: false
  //   }
  // },

  // Chart: {
  //   label: 'Chart',
  //   description: 'Data visualization charts and graphs',
  //   icon: 'bar_chart',
  //   category: 'data',
  //   schema: z.object({
  //     type: z.enum(['bar', 'line', 'pie', 'doughnut', 'area']).default('bar'),
  //     title: z.string().optional(),
  //     data: z.array(z.object({
  //       label: z.string(),
  //       value: z.number()
  //     })).default([]),
  //     width: z.number().positive().optional(),
  //     height: z.number().positive().default(300),
  //     showLegend: z.boolean().default(true),
  //     showTooltips: z.boolean().default(true)
  //   }),
  //   defaultProps: {
  //     type: 'bar',
  //     data: [
  //       { label: 'Category 1', value: 10 },
  //       { label: 'Category 2', value: 20 },
  //       { label: 'Category 3', value: 15 }
  //     ],
  //     height: 300,
  //     showLegend: true,
  //     showTooltips: true
  //   }
  // },

  // Form: {
  //   label: 'Form',
  //   description: 'Interactive forms with validation',
  //   icon: 'assignment',
  //   category: 'interactive',
  //   schema: z.object({
  //     title: z.string().optional(),
  //     fields: z.array(z.object({
  //       name: z.string(),
  //       label: z.string(),
  //       type: z.enum(['text', 'email', 'number', 'textarea', 'select', 'checkbox']),
  //       required: z.boolean().default(false),
  //       placeholder: z.string().optional(),
  //       options: z.array(z.string()).optional()
  //     })).default([]),
  //     submitText: z.string().default('Submit'),
  //     action: z.string().optional(),
  //     method: z.enum(['GET', 'POST']).default('POST')
  //   }),
  //   defaultProps: {
  //     fields: [
  //       { name: 'name', label: 'Name', type: 'text', required: true },
  //       { name: 'email', label: 'Email', type: 'email', required: true }
  //     ],
  //     submitText: 'Submit',
  //     method: 'POST'
  //   }
  // },

  // Table: {
  //   label: 'Table',
  //   description: 'Structured data in rows and columns',
  //   icon: 'table_chart',
  //   category: 'data',
  //   schema: z.object({
  //     title: z.string().optional(),
  //     columns: z.array(z.object({
  //       key: z.string(),
  //       label: z.string(),
  //       sortable: z.boolean().default(false)
  //     })).default([]),
  //     rows: z.array(z.record(z.any())).default([]),
  //     striped: z.boolean().default(false),
  //     bordered: z.boolean().default(true),
  //     hover: z.boolean().default(false),
  //     pagination: z.boolean().default(false),
  //     pageSize: z.number().min(5).max(100).default(10)
  //   }),
  //   defaultProps: {
  //     columns: [
  //       { key: 'name', label: 'Name', sortable: true },
  //       { key: 'value', label: 'Value', sortable: true }
  //     ],
  //     rows: [
  //       { name: 'Item 1', value: '100' },
  //       { name: 'Item 2', value: '200' }
  //     ],
  //     striped: false,
  //     bordered: true,
  //     hover: false,
  //     pagination: false,
  //     pageSize: 10
  //   }
  // },

  Chat: {
    label: 'Chat',
    description: 'Ask questions about this view’s content and get cited answers',
    icon: 'chat',
    category: 'interactive',
    schema: z.object({
      scopeBlockIds: z.array(z.string()).optional(),
      visibility: z.enum(['internal', 'external']).default('internal'),
      title: z.string().default('Ask about this content'),
      placeholder: z.string().default('Ask a question…'),
      height: z.coerce.number().min(200).max(900).default(420),
      showCitations: z.boolean().default(true)
    }),
    defaultProps: {
      visibility: 'internal',
      title: 'Ask about this content',
      placeholder: 'Ask a question…',
      height: 420,
      showCitations: true
    }
  },

  Embed: {
    label: 'Embed',
    description: 'Embed external content via iframe',
    icon: 'code',
    category: 'content',
    schema: z.object({
      src: z.string().default('').describe('Enter the URL to embed (e.g., https://example.com)'),
      title: z.string().optional().default('Embedded Content'),
      width: z.coerce.number().positive().optional(),
      height: z.coerce.number().positive().default(400),
      allowFullscreen: z.boolean().default(true),
      allowScripts: z.boolean().default(true).describe('Allow embedded content to run JavaScript'),
      allowForms: z.boolean().default(true).describe('Allow embedded content to submit forms'),
      allowSameOrigin: z.boolean().default(true).describe('Allow embedded content to access cookies and storage'),
      allowPopups: z.boolean().default(true).describe('Allow embedded content to open new windows'),
      allowModals: z.boolean().default(true).describe('Allow embedded content to show dialogs'),
      allowDownloads: z.boolean().default(true).describe('Allow embedded content to trigger downloads'),
      loading: z.enum(['lazy', 'eager']).default('lazy')
    }),
    defaultProps: {
      src: '',
      title: 'Embedded Content',
      height: 400,
      allowFullscreen: true,
      allowScripts: true,
      allowForms: true,
      allowSameOrigin: true,
      allowPopups: true,
      allowModals: true,
      allowDownloads: true,
      loading: 'lazy'
    }
  },

  // DownloadAll: {
  //   label: 'Download All',
  //   description: 'Bulk download functionality for content',
  //   icon: 'download',
  //   category: 'interactive',
  //   schema: z.object({
  //     buttonText: z.string().default('Download All'),
  //     variant: z.enum(['contained', 'outlined', 'text']).default('contained'),
  //     color: z.enum(['primary', 'secondary']).default('primary'),
  //     includeFilters: z.boolean().default(true),
  //     format: z.enum(['zip', 'pdf']).default('zip'),
  //     maxItems: z.number().min(1).max(1000).default(100)
  //   }),
  //   defaultProps: {
  //     buttonText: 'Download All',
  //     variant: 'contained',
  //     color: 'primary',
  //     includeFilters: true,
  //     format: 'zip',
  //     maxItems: 100
  //   }
  // }
};

// Helper functions for registry operations
export const getComponentMeta = (type: ComponentType): ComponentMeta => {
  return ComponentRegistry[type];
};

export const validateComponentProps = (type: ComponentType, props: unknown) => {
  const meta = getComponentMeta(type);
  return meta.schema.safeParse(props);
};

export const getDefaultProps = (type: ComponentType) => {
  return getComponentMeta(type).defaultProps;
};

export const getComponentsByCategory = (category: ComponentMeta['category']) => {
  return Object.entries(ComponentRegistry)
    .filter(([, meta]) => meta.category === category)
    .map(([type, meta]) => ({ type: type as ComponentType, ...meta }));
};

export const getAllComponents = () => {
  return Object.entries(ComponentRegistry).map(([type, meta]) => ({
    type: type as ComponentType,
    ...meta
  }));
};

// Type exports for form generation
export type ComponentPropsType<T extends ComponentType> = z.infer<typeof ComponentRegistry[T]['schema']>;
export type AllComponentProps = {
  [K in ComponentType]: ComponentPropsType<K>;
};